import { UserContext } from "@context/User/UserContext";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Box, Grid, LinearProgress, Typography } from "@mui/material";
import { chatManagerApiService } from "@services/chatManagerApi";
import { useContext, useEffect, useState } from "react";

import AvatarChatBox from "@components/InteractiveAvatar/components/AvatarChatBox/AvatarChatBox";
import { useTranslation } from "react-i18next";
import {
  AvatarChatHistoryMessageSource,
  IAvatarChatHistoryMessage
} from "./components/AvatarChatHistory";
import AvatarSettings from "./components/AvatarSettings";
import { ModalSummary } from "./ModalSummary";

export const InteractiveAvatar = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(UserContext);

  const [isLoadingSession] = useState(false);
  const [isLoadingRepeat, setIsLoadingRepeat] = useState(false);
  const [text, setText] = useState<string>("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [openSummary, setOpenSummary] = useState(false);
  const [chatHistory, setChatHistory] = useState<IAvatarChatHistoryMessage[]>([
    {
      source: AvatarChatHistoryMessageSource.AVATAR,
      text: t("chatbot.common.welcome_message", {
        userName: currentUser?.firstName
      }),
      timestamp: new Date()
    }
  ]);

  const [sessionId, setSessionId] = useState<string | null>(null);

  async function endSession() {
    setAudioBlob(null);
    setChatHistory([
      {
        source: AvatarChatHistoryMessageSource.AVATAR,
        text: t("chatbot.common.welcome_message", {
          userName: currentUser?.firstName
        }),
        timestamp: new Date()
      }
    ]);
    setSessionId(null);
  }

  const handleRecord = (blob: Blob) => {
    setAudioBlob(blob);
  };

  const handleDeleteAudio = () => {
    setAudioBlob(null);
  };

  const addUserMessageToHistory = (
    messageText: string
  ): IAvatarChatHistoryMessage[] => {
    const userMessage = {
      source: AvatarChatHistoryMessageSource.USER,
      text: messageText,
      timestamp: new Date()
    };
    const newHistory = [...chatHistory, userMessage];
    setChatHistory(newHistory);
    return newHistory;
  };

  const updateTranscriptionInHistory = (
    currentHistory: IAvatarChatHistoryMessage[],
    transcription: string | null | undefined
  ): IAvatarChatHistoryMessage[] => {
    if (!transcription) return currentHistory;

    const updatedHistory = [...currentHistory];
    const lastUserMessageIndex =
      updatedHistory
        .map((msg, index) => ({ msg, index }))
        .filter(
          (item) => item.msg.source === AvatarChatHistoryMessageSource.USER
        )
        .pop()?.index ?? -1;

    if (lastUserMessageIndex !== -1) {
      updatedHistory[lastUserMessageIndex] = {
        ...updatedHistory[lastUserMessageIndex],
        text: transcription || t("chatbot.common.transcription_error")
      };
    }

    return updatedHistory;
  };

  const addAvatarResponseToHistory = (
    currentHistory: IAvatarChatHistoryMessage[],
    responseText: string
  ): void => {
    setChatHistory([
      ...currentHistory,
      {
        source: AvatarChatHistoryMessageSource.AVATAR,
        text: responseText,
        timestamp: new Date()
      }
    ]);
  };

  const handleVoiceMessage = async (): Promise<void> => {
    if (!audioBlob || !sessionId) return;

    const initialHistory = addUserMessageToHistory(
      t("chatbot.common.voice_record")
    );

    const default_language = "spanish";

    try {
      const chatResponse =
        await chatManagerApiService.sendChatRequestWithAudioOrText(
          sessionId,
          default_language,
          undefined,
          audioBlob
        );

      // Update chat history with transcription
      const updatedHistory = updateTranscriptionInHistory(
        initialHistory,
        chatResponse.user_transcription
      );

      // Add avatar response
      addAvatarResponseToHistory(updatedHistory, chatResponse.response);
    } catch (error) {
      console.error("Error during speech processing:", error);
    }

    handleDeleteAudio();
  };

  const handleTextMessage = async (): Promise<void> => {
    if (!text || !sessionId) return;

    const default_language = "spanish";

    const newHistory = addUserMessageToHistory(text);

    try {
      const chatResponse =
        await chatManagerApiService.sendChatRequestWithAudioOrText(
          sessionId,
          default_language,
          text,
          undefined
        );

      // Add avatar response
      addAvatarResponseToHistory(newHistory, chatResponse.response);
    } catch (error) {
      console.error("Error during speech processing:", error);
    }
  };

  async function handleSpeak() {
    setIsLoadingRepeat(true);
    console.log(">> handleSpeak()");

    if (audioBlob) {
      await handleVoiceMessage();
    } else {
      await handleTextMessage();
    }

    setIsLoadingRepeat(false);
  }

  useEffect(() => {
    console.log(">> session id changed", sessionId);
  }, [sessionId]);

  const handleOpenSummary = () => {
    setOpenSummary(true);
  };

  const handleCloseSummary = () => {
    setOpenSummary(false);
  };

  async function startSession() {
    setSessionId(crypto.randomUUID());
  }

  return (
    <Box sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
      {isLoadingSession && (
        <Box className="w-full">
          <LinearProgress color="primary" />
        </Box>
      )}
      {!sessionId && <AvatarSettings startSession={startSession} />}
      {sessionId && sessionId.length >= 1 && (
        <Grid
          container
          spacing={2}
          display="flex"
          justifyContent="center"
          alignContent="flex-end"
          alignItems="flex-end"
          sx={{ height: "100%", width: "100%", marginTop: 7 }}
        >
          <Grid item xs={12} sm={10} md={10} height={"64vh"}>
            <Box
              display="flex"
              mb={1}
              onClick={endSession}
              sx={{ cursor: "pointer" }}
            >
              <ChevronLeftIcon
                color="primary"
                sx={{ fontSize: { xs: 18, sm: 22 } }}
              />
              <Typography
                color="primary"
                fontWeight={700}
                sx={{ fontSize: { xs: 12, sm: 15 } }}
              >
                {t("chatbot.actions.return_avatar_config")}
              </Typography>
            </Box>
            <AvatarChatBox
              messages={chatHistory}
              disabled={!sessionId}
              loading={isLoadingRepeat}
              text={text}
              setText={setText}
              handleSpeak={handleSpeak}
              handleOpenSummary={handleOpenSummary}
              handleRecord={handleRecord}
              audioBlob={audioBlob}
              handleDeleteAudio={handleDeleteAudio}
            />
          </Grid>
        </Grid>
      )}
      {openSummary && (
        <ModalSummary
          open={openSummary}
          onClose={handleCloseSummary}
          sessionId={sessionId}
        />
      )}
    </Box>
  );
};

export default InteractiveAvatar;
